/* eslint-disable react/prop-types */
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CampaignReadyModal,
  DiscountModal,
  ExitFeedbackModal,
  PaymentSuccessModal,
} from '../../../../components/CampaignUpsellModals'
import {
  UPSELL_FLOWS,
  useUpsellCatalog,
  useUpgradePlansTab,
  useUpsellEventMap,
  useUpsellFlowState,
  useUpsellCheckout,
  useUpsellSuccessClose,
  trackUpsellExitFeedback,
  resolvePlanCheckoutPayload,
  resolvePackageCheckoutPayload,
} from '../UpsellFlows'
import {
  buildCouponPackageOffer,
  buildMarketComparisonPrice,
  getPackagePriceValue,
  UPSELL_CAMPAIGN_PACKAGE_CODE,
  UPSELL_FEATURED_PACKAGE_CODE,
  UPSELL_FEATURED_PLAN_TIER,
  filterStaticPlanOptions,
} from '../UpsellFlows/upsellCatalog'
import {
  PLAN_OPTIONS,
} from '../UpsellFlows/sharedConstants'
import {
  ARTS_LIMIT_BONUS_OFFER_CODE,
  ARTS_LIMIT_UPSELL_STEPS,
  BONUS_PACKAGE,
  SMALLER_OFFER,
  SMALLER_PACKAGE,
} from './constants'

export const ArtsLimitUpsellFlow = forwardRef(function ArtsLimitUpsellFlow(
  { enabled = false, campaignId = null, onDismiss },
  ref,
) {
  const { t } = useTranslation()
  const catalog = useUpsellCatalog({ enabled })
  const {
    openSubscriptionCheckout,
    openAddonCheckout,
  } = useUpsellCheckout()
  const {
    activeTab,
    setActiveTab,
    closeFlow: resetFlow,
    openFlow,
    isStep,
    goToStep,
    openSuccessStep,
    currentStep,
    isSuccessStepActive,
  } = useUpsellFlowState({
    enabled,
    initialStep: ARTS_LIMIT_UPSELL_STEPS.READY,
    initialTab: 'packages',
  })
  const upsellTabs = useUpgradePlansTab(
    catalog,
    activeTab,
    setActiveTab,
    [
      { id: 'packages', label: t('upsell.tabs.packages') },
      { id: 'plans', label: t('upsell.tabs.plans') },
    ],
  )

  const exitFeedbackOptions = useMemo(
    () => [
      { id: 'price', label: t('upsell.exit_feedback.price') },
      { id: 'trial', label: t('upsell.exit_feedback.trial') },
      { id: 'quality', label: t('upsell.exit_feedback.quality') },
      { id: 'exploring', label: t('upsell.exit_feedback.exploring') },
    ],
    [t],
  )

  useUpsellEventMap({
    flow: UPSELL_FLOWS.ARTS_LIMIT,
    campaignId,
    currentStep,
    activeTab,
  })

  const featuredPlanOffer = useMemo(
    () => catalog.buildFeaturedPlanOffer(),
    [catalog],
  )

  const featuredPackageOffer = useMemo(
    () => catalog.buildFeaturedPackageOffer(catalog.featuredPackage),
    [catalog],
  )

  const packageMarketPrice = useMemo(
    () => buildMarketComparisonPrice(catalog.featuredPackage),
    [catalog.featuredPackage],
  )

  const planMarketPrice = useMemo(() => {
    const plan = catalog.featuredUpgradePlan ?? catalog.featuredPlan
    if (!plan?.price) return null
    return catalog.formatPrice(plan.price, plan.currency)
  }, [catalog.featuredPlan, catalog.featuredUpgradePlan, catalog])

  const packageOptions = useMemo(
    () => catalog.getPackageOptions([UPSELL_FEATURED_PACKAGE_CODE]),
    [catalog],
  )

  const featuredUpgradePlan = catalog.featuredUpgradePlan

  const planOptions = useMemo(() => {
    const featuredTier =
      catalog.featuredUpgradePlan?.tier ?? UPSELL_FEATURED_PLAN_TIER

    if (!catalog.dbPlans.length) {
      return filterStaticPlanOptions(
        PLAN_OPTIONS,
        catalog.currentPlanTier,
        featuredTier,
      )
    }

    return catalog.getUpgradePlanOptions()
  }, [catalog])

  const featuredPackageArts = featuredPackageOffer?.arts

  const bonusOffer = useMemo(
    () => ({
      description: t('upsell.arts_limit.bonus_description'),
      price: featuredPackageOffer?.price,
      priceValue: getPackagePriceValue(catalog.featuredPackage),
      timerSeconds: SMALLER_OFFER.timerSeconds,
    }),
    [catalog.featuredPackage, featuredPackageOffer?.price, t],
  )

  const smallerOffer = useMemo(
    () =>
      buildCouponPackageOffer(catalog.campaignPackage, {
        couponCode: SMALLER_OFFER.couponCode,
        timerSeconds: SMALLER_OFFER.timerSeconds,
      }),
    [catalog.campaignPackage],
  )

  const hideFlow = resetFlow

  const closeFlow = useCallback(() => {
    resetFlow()
    onDismiss?.()
  }, [onDismiss, resetFlow])

  const closeSuccessFlow = useUpsellSuccessClose(closeFlow, isSuccessStepActive)

  const handleOpenPackageCheckout = useCallback((
    option,
    upsellSuccessStep = ARTS_LIMIT_UPSELL_STEPS.SUCCESS_PACKAGE,
  ) => {
    openAddonCheckout({
      ...resolvePackageCheckoutPayload(
        option,
        catalog.featuredPackage,
        UPSELL_FEATURED_PACKAGE_CODE,
      ),
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.ARTS_LIMIT,
      upsellSuccessStep,
      onOpen: hideFlow,
    })
  }, [campaignId, catalog.featuredPackage, hideFlow, openAddonCheckout])

  const handleOpenPlanCheckout = useCallback((option) => {
    openSubscriptionCheckout({
      ...resolvePlanCheckoutPayload(option, featuredUpgradePlan),
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.ARTS_LIMIT,
      upsellSuccessStep: ARTS_LIMIT_UPSELL_STEPS.SUCCESS_PACKAGE,
      onOpen: hideFlow,
    })
  }, [campaignId, featuredUpgradePlan, hideFlow, openSubscriptionCheckout])

  const handleOpenFeaturedPackageCheckout = useCallback(() => {
    handleOpenPackageCheckout(
      catalog.featuredPackage,
      ARTS_LIMIT_UPSELL_STEPS.SUCCESS_PACKAGE,
    )
  }, [catalog.featuredPackage, handleOpenPackageCheckout])

  const handleOpenBonusPackageCheckout = useCallback(() => {
    openAddonCheckout({
      ...resolvePackageCheckoutPayload(
        null,
        catalog.featuredPackage,
        UPSELL_FEATURED_PACKAGE_CODE,
      ),
      price: bonusOffer.priceValue,
      offerCode: ARTS_LIMIT_BONUS_OFFER_CODE,
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.ARTS_LIMIT,
      upsellSuccessStep: ARTS_LIMIT_UPSELL_STEPS.SUCCESS_BONUS,
      onOpen: hideFlow,
    })
  }, [campaignId, catalog.featuredPackage, hideFlow, openAddonCheckout])

  const handleOpenSmallerPackageCheckout = useCallback(() => {
    openAddonCheckout({
      ...resolvePackageCheckoutPayload(
        null,
        catalog.campaignPackage,
        UPSELL_CAMPAIGN_PACKAGE_CODE,
      ),
      price: smallerOffer?.priceValue,
      promotionCode: smallerOffer?.couponCode ?? SMALLER_OFFER.couponCode,
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.ARTS_LIMIT,
      upsellSuccessStep: ARTS_LIMIT_UPSELL_STEPS.SUCCESS_SMALLER,
      onOpen: hideFlow,
    })
  }, [campaignId, catalog.campaignPackage, hideFlow, openAddonCheckout, smallerOffer])

  useImperativeHandle(ref, () => ({
    open: openFlow,
    close: closeFlow,
    openSuccessStep,
  }), [closeFlow, openFlow, openSuccessStep])

  if (!enabled && !isSuccessStepActive) {
    return null
  }

  const packagesContent = {
    title: t('upsell.arts_limit.title'),
    subtitle: t('upsell.arts_limit.subtitle'),
    marketLabel: t('upsell.arts_limit.market_label_packages'),
    marketPrice: packageMarketPrice
      ? t('upsell.market_from_price', { price: packageMarketPrice })
      : null,
    platformLabel: t('upsell.arts_limit.platform_label'),
    platformPrice: featuredPackageOffer?.price,
    featuredOffer: featuredPackageOffer,
    otherOptionsTitle: t('upsell.arts_limit.other_packages_title'),
    otherOptions: packageOptions,
    onSelectOption: handleOpenPackageCheckout,
    primaryAction: {
      label: t('upsell.arts_limit.continue_creating'),
      onClick: handleOpenFeaturedPackageCheckout,
    },
    tertiaryLink: catalog.hasSelectablePlans
      ? {
        label: t('upsell.arts_limit.weekly_plan_link'),
        onClick: () => setActiveTab('plans'),
      }
      : undefined,
    skipAction: {
      label: t('upsell.arts_limit.skip_generating'),
      onClick: () => goToStep(ARTS_LIMIT_UPSELL_STEPS.BONUS),
    },
  }

  const plansContent = {
    title: t('upsell.arts_limit.title'),
    subtitle: t('upsell.arts_limit.subtitle'),
    marketLabel: t('upsell.arts_limit.market_label_plans'),
    marketPrice: planMarketPrice,
    platformLabel: featuredPlanOffer?.name
      ? t('upsell.arts_limit.plan_label', { plan: featuredPlanOffer.name })
      : t('upsell.arts_limit.plan_label', { plan: 'Pro' }),
    platformPrice: featuredPlanOffer?.price,
    featuredOffer: featuredPlanOffer,
    otherOptionsTitle: t('upsell.tabs.plans'),
    otherOptions: planOptions,
    onSelectOption: handleOpenPlanCheckout,
    primaryAction: {
      label: t('upsell.arts_limit.subscribe_continue'),
      onClick: () => handleOpenPlanCheckout(featuredUpgradePlan),
    },
    tertiaryLink: {
      label: t('upsell.arts_limit.packages_link'),
      onClick: () => setActiveTab('packages'),
    },
    skipAction: {
      label: t('upsell.arts_limit.skip_generating'),
      onClick: () => goToStep(ARTS_LIMIT_UPSELL_STEPS.BONUS),
    },
  }

  return (
    <>
      <CampaignReadyModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.READY)}
        onClose={() => goToStep(ARTS_LIMIT_UPSELL_STEPS.BONUS)}
        tabs={upsellTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        packages={packagesContent}
        plans={plansContent}
      />

      <DiscountModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.BONUS)}
        onClose={() => goToStep(ARTS_LIMIT_UPSELL_STEPS.SMALLER)}
        title={t('upsell.arts_limit.bonus_title')}
        subtitle={t('upsell.arts_limit.bonus_subtitle')}
        offer={bonusOffer}
        primaryAction={{
          label: t('upsell.arts_limit.bonus_cta'),
          onClick: handleOpenBonusPackageCheckout,
        }}
        dismissAction={{
          label: t('upsell.arts_limit.not_now'),
          onClick: () => goToStep(ARTS_LIMIT_UPSELL_STEPS.SMALLER),
        }}
      />

      <DiscountModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.SMALLER)}
        onClose={() => goToStep(ARTS_LIMIT_UPSELL_STEPS.EXIT)}
        title={t('upsell.arts_limit.smaller_title')}
        subtitle={t('upsell.arts_limit.smaller_subtitle')}
        offer={smallerOffer}
        footnote={t('upsell.arts_limit.coupon_footnote')}
        primaryAction={{
          label: t('upsell.arts_limit.smaller_cta'),
          onClick: handleOpenSmallerPackageCheckout,
        }}
        dismissAction={{
          label: t('upsell.arts_limit.exit_anyway'),
          onClick: () => goToStep(ARTS_LIMIT_UPSELL_STEPS.EXIT),
        }}
      />

      <ExitFeedbackModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.EXIT)}
        onClose={closeFlow}
        title={t('upsell.arts_limit.exit_title')}
        description={t('upsell.arts_limit.exit_description')}
        feedback={{
          options: exitFeedbackOptions,
        }}
        onExitWithFeedback={(reason) =>
          trackUpsellExitFeedback({
            flow: UPSELL_FLOWS.ARTS_LIMIT,
            campaignId,
            reasonId: reason.id,
            reasonLabel: reason.label,
          })
        }
        secondaryAction={{
          label: t('upsell.arts_limit.back_to_platform'),
          onClick: closeFlow,
        }}
      />

      <PaymentSuccessModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.SUCCESS_PACKAGE)}
        onClose={closeSuccessFlow}
        title={t('upsell.arts_limit.success_title')}
        description={t('upsell.arts_limit.success_package', { count: featuredPackageArts })}
        primaryAction={{
          label: t('upsell.arts_limit.back_to_campaign'),
          onClick: closeSuccessFlow,
        }}
      />

      <PaymentSuccessModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.SUCCESS_BONUS)}
        onClose={closeSuccessFlow}
        title={t('upsell.arts_limit.success_title')}
        description={t('upsell.arts_limit.success_bonus', {
          total: BONUS_PACKAGE.totalArts,
          packageArts: BONUS_PACKAGE.packageArts,
          bonusArts: BONUS_PACKAGE.bonusArts,
        })}
        primaryAction={{
          label: t('upsell.arts_limit.back_to_campaign'),
          onClick: closeSuccessFlow,
        }}
      />

      <PaymentSuccessModal
        isOpen={isStep(ARTS_LIMIT_UPSELL_STEPS.SUCCESS_SMALLER)}
        onClose={closeSuccessFlow}
        title={t('upsell.arts_limit.success_title')}
        description={t('upsell.arts_limit.success_smaller', {
          count: SMALLER_PACKAGE.artsCount,
          price: smallerOffer?.price,
          coupon: SMALLER_OFFER.couponCode,
        })}
        primaryAction={{
          label: t('upsell.arts_limit.back_to_campaign'),
          onClick: closeSuccessFlow,
        }}
      />
    </>
  )
})

export default ArtsLimitUpsellFlow
